/**
 * Connection Actions Menu Component
 *
 * Three-dot dropdown menu for connection actions.
 * Actions are dynamically filtered based on display state from computeDisplayState().
 *
 * Supported actions:
 * - test: Test connection (Connected state)
 * - pause: Pause syncing (Connected state)
 * - unpause: Resume syncing (Paused state)
 * - reconnect: Re-authorize (Needs Attention, Setup Incomplete states)
 * - restore: Restore from archive (Archived state)
 * - delete: Remove connection (all states)
 */

import { Dropdown, App, Typography } from 'antd'
import {
  MoreOutlined,
  SyncOutlined,
  DeleteOutlined,
  ApiOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  InboxOutlined,
} from '@ant-design/icons'
import type { MenuProps } from 'antd'
import type { Connection, ConnectionAction, DisplayState } from '@/types/connection.types'
import { computeDisplayState } from '@/utils/connectionState'

const { Text } = Typography

/**
 * Props for ConnectionActionsMenu component
 */
export interface ConnectionActionsMenuProps {
  /** Connection entity */
  connection: Connection

  /** Callback for action selection */
  onAction: (action: ConnectionAction, connection: Connection) => void

  /** Disable all actions (loading state) */
  disabled?: boolean

  /** Custom style */
  style?: React.CSSProperties

  /** Custom class name */
  className?: string
}

/**
 * Action confirmation modal configuration
 * Simplified to only show confirmations for destructive actions
 */
const actionConfirmations: Partial<Record<
  ConnectionAction,
  {
    title: string
    content: (platformName: string) => string
    okText: string
    okType?: 'primary' | 'danger'
    icon?: React.ReactNode
  }
>> = {
  reconnect: {
    title: 'Reconnect Platform',
    content: (platform) =>
      `You'll be redirected to re-authorize ${platform}. Your connection settings will be preserved.`,
    okText: 'Continue',
    okType: 'primary',
  },
  delete: {
    title: 'Remove Connection',
    content: (platform) =>
      `Remove your ${platform} connection? You can reconnect anytime.`,
    okText: 'Remove',
    okType: 'danger',
  },
  test: {
    title: 'Test Connection',
    content: (platform) =>
      `This will verify ${platform} can communicate with the platform API.`,
    okText: 'Test',
    okType: 'primary',
  },
  pause: {
    title: 'Pause Connection',
    content: (platform) =>
      `Pause syncing with ${platform}? You can resume anytime.`,
    okText: 'Pause',
    okType: 'primary',
  },
  unpause: {
    title: 'Resume Connection',
    content: (platform) =>
      `Resume syncing with ${platform}? Your data will start syncing again.`,
    okText: 'Resume',
    okType: 'primary',
  },
  restore: {
    title: 'Restore Connection',
    content: (platform) =>
      `Restore your ${platform} connection from archive?`,
    okText: 'Restore',
    okType: 'primary',
  },
}

/**
 * Action menu item configuration
 * Maps connection actions to UI elements
 */
const actionMenuItems: Partial<Record<
  ConnectionAction,
  {
    label: string
    icon: React.ReactNode
    danger?: boolean
  }
>> = {
  reconnect: {
    label: 'Reconnect',
    icon: <SyncOutlined />,
  },
  delete: {
    label: 'Remove Connection',
    icon: <DeleteOutlined />,
    danger: true,
  },
  test: {
    label: 'Test Connection',
    icon: <ApiOutlined />,
  },
  pause: {
    label: 'Pause Syncing',
    icon: <PauseCircleOutlined />,
  },
  unpause: {
    label: 'Resume Syncing',
    icon: <PlayCircleOutlined />,
  },
  restore: {
    label: 'Restore Connection',
    icon: <InboxOutlined />,
  },
}

/**
 * Show confirmation modal for action
 */
function confirmAction(
  modalApi: ReturnType<typeof App.useApp>['modal'],
  action: ConnectionAction,
  platformName: string,
  onConfirm: () => void
): void {
  const config = actionConfirmations[action]

  if (!config) {
    // No confirmation needed, execute immediately
    onConfirm()
    return
  }

  modalApi.confirm({
    title: config.title,
    content: config.content(platformName),
    okText: config.okText,
    okType: config.okType || 'primary',
    cancelText: 'Cancel',
    icon: config.icon,
    onOk: onConfirm,
  })
}

/**
 * Connection Actions Menu
 *
 * Simplified dropdown menu showing available actions for connection.
 * Actions automatically filtered based on display state.
 * Shows confirmation modals with platform name for better UX.
 *
 * @example
 * ```tsx
 * <ConnectionActionsMenu
 *   connection={connection}
 *   onAction={(action, conn) => {
 *     if (action === 'delete') {
 *       deleteMutation.mutate(conn.uid)
 *     }
 *   }}
 * />
 * ```
 */
export const ConnectionActionsMenu: React.FC<ConnectionActionsMenuProps> = ({
  connection,
  onAction,
  disabled = false,
  style,
  className,
}) => {
  const { modal } = App.useApp()

  // Compute display state to get available actions
  const displayState: DisplayState = computeDisplayState(connection)

  // Get platform display name for modals
  const platformName = connection.platform.replace('-sandbox', '').replace(/^\w/, c => c.toUpperCase())

  // Build menu items from available actions
  const menuItems: MenuProps['items'] = displayState.actions
    .filter((action) => actionMenuItems[action]) // Only show actions we have UI for
    .map((action) => {
      const config = actionMenuItems[action]!

      return {
        key: action,
        label: config.label,
        icon: config.icon,
        danger: config.danger,
        onClick: () => {
          // Show confirmation modal
          confirmAction(modal, action, platformName, () => {
            onAction(action, connection)
          })
        },
      }
    })

  // If no actions available, show disabled menu
  if (menuItems.length === 0) {
    return (
      <Text type="secondary" style={{ fontSize: '20px', cursor: 'not-allowed', ...style }}>
        <MoreOutlined />
      </Text>
    )
  }

  return (
    <Dropdown
      menu={{ items: menuItems }}
      trigger={['click']}
      disabled={disabled}
      placement="bottomRight"
    >
      <span
        style={{
          fontSize: '20px',
          cursor: disabled ? 'not-allowed' : 'pointer',
          color: disabled ? '#d9d9d9' : 'rgba(0, 0, 0, 0.45)',
          ...style,
        }}
        className={className}
        onClick={(e) => e.stopPropagation()} // Prevent card click when clicking menu
      >
        <MoreOutlined />
      </span>
    </Dropdown>
  )
}

export default ConnectionActionsMenu
